
import { tokenStore } from "@/utils/token-store";
import router from "@/router";
import { ServerConfig } from "@/config/config";
import {Dossier} from "@/models/dossier/dossier";
import {createAxiosClient} from "@/utils/axios";
import {UserInfo} from "@/models/dossier/user-info";
import {Company} from "@/models/company";
import { EventEmitter } from "@/utils/event-emitter";
import {EVENT_SWITCH_SESSION, MainSocketService} from "@/services/main-socket-service";
import {AuthSocketService} from "@/services/auth-socket-service";
import {HttpError} from "@/utils/services/http-error";
import {SocketWorkerInterface} from "@/utils/services/socket-worker-interface";

export interface ILoginOptions {
    workspaceUrl?:string;
    totp?:string;
}

const unauthenticatedClient = createAxiosClient({skipAuthentication: true});
export class AuthServiceClass extends EventEmitter {
    public isLoggedIn:boolean = false;
    public wfDossier:Dossier = new Dossier();

	public wfCompany:Company = new Company();
    public wfUser:UserInfo = new UserInfo();

	private _appReady:boolean = false;
	public get appReady():boolean {
		return this._appReady;
	}
	private set appReady(value:boolean) {
		this._appReady = value;
		for (let i = this.appReadyPromises.length -1; i>=0; i--){
			this.appReadyPromises[i]();
			this.appReadyPromises.splice(i, 1);
		}
	}

	private appReadyPromises:Function[] = [];

	private get baseUrl():string{
    	return ServerConfig.auth + "/time-track/auth";
	}

	private devModeEnabled:boolean = false;
	public get isDevModeEnabled():boolean {
		if (this.devModeEnabled) return true;
		return localStorage.getItem("dev-mode") == "true";
	}
	private refreshingTokens:boolean = false;

	async login(username: string, password: string, options:ILoginOptions = {}) {
    	let response = await unauthenticatedClient.post(this.baseUrl + "/login",
			{Username: username,Password: password, workspace: options.workspaceUrl, totpPassword: options.totp});
    	tokenStore.refreshToken = response.data.RefreshToken;
		console.log(response.data.AccessTokens);
    	if (response.data.AccessTokens.length == 0) {
    		return this.onNoDossiers();
    	}
    	tokenStore.accessTokens = response.data.AccessTokens.map((t: any) => {
    		return {
    			dossierName: t.DossierName,
    			token: t.Token,
    		};
    	});
    	tokenStore.tokenExpires = new Date(response.data.AccessTokensExpire);
    	tokenStore.refreshTokenExpires = new Date(response.data.RefreshTokenExpires);
    	this.wfDossier = new Dossier(response.data.AccessTokens[0]);
    	tokenStore.selectedDossier = this.wfDossier.name;
    	this.isLoggedIn = true;
    	this.devModeEnabled = false;
		await router.push("/");
	}
	async selectDossier(newDossier: string) {
		if (tokenStore.accessTokens.map(t => t.dossierName).indexOf(newDossier) == -1) {
			throw new Error("Dossier does not exist");
		}
		await MainSocketService.disconnect();
		await AuthSocketService.disconnect();
		tokenStore.selectedDossier = newDossier;
		if (this.wfDossier){
			this.wfDossier.name = newDossier;
		}
		MainSocketService.setAccessToken(tokenStore.getSelectedToken().token);
		await this.getUserInfo();
		await MainSocketService.connect();
		MainSocketService.emit(EVENT_SWITCH_SESSION);
		await this.initServices();
	}

	private async initServices() {
		this.appReady = false;
		MainSocketService.setAccessToken(tokenStore.getSelectedToken().token);
		AuthSocketService.setAccessToken(tokenStore.getSelectedToken().token);
		await MainSocketService.connect();
		MainSocketService.emit(EVENT_SWITCH_SESSION);
		await AuthSocketService.connect();
		this.appReady = true;
	}

	async init() {
		let searchParams = new URLSearchParams(window.location.search);
		let queryRefreshToken = searchParams.get("refreshToken");
		if (queryRefreshToken) {
			if (tokenStore.refreshToken){
				await this.logout();
			}
			tokenStore.refreshToken = queryRefreshToken;
			searchParams.delete("refreshToken");
			window.location.search = searchParams.toString();
			//	await new Promise((resolve)=>{
			//		setTimeout(resolve, 250);
			//	});
		}

		if (tokenStore.refreshToken) {
			this.isLoggedIn = true;
			//	if (goToRootPage){
			//		router.push("/");
			//	}
			//await this.initProfileAfterStart();
		}
	}

	private onNoDossiers(){
    	router.push("/no-dossiers");
    	this.logout();
    	this.emit("error", "no-dossiers");
	}

	async getUserInfo() {
		/*let postResult = await axios.get(this.baseUrl);
		this.wfUser = new UserInfo(postResult.data.User);
		this.wfDossier = new Dossier(postResult.data.Dossier);
		this.wfCompany = new Company(postResult.data.Company);*/
	}

	public refreshTokens = async () => {
		if (this.refreshingTokens) {
			return this.onTokensRefreshed();
		}
		try {
			this.refreshingTokens = true;
			let response = await unauthenticatedClient.post(this.baseUrl + "/refresh-token", {}, {
				headers: {
					Authorization: tokenStore.refreshToken
				}
			});
			tokenStore.accessTokens = response.data.AccessTokens.map((t: any) => {
				return {
					dossierName: t.Dossier,
					token: t.Token,
				};
			});
			tokenStore.tokenExpires = new Date(response.data.Expires);
			this.refreshingTokens = false;
			this.devModeEnabled = response.data.DevModeEnabled;
			this.emit("refresh-tokens-done", true);
			MainSocketService.setAccessToken(tokenStore.getSelectedToken().token);
			AuthSocketService.setAccessToken(tokenStore.getSelectedToken().token);
		} catch (err) {
			this.refreshingTokens = false;
			let error = err as HttpError;
			if (error.status == 401 || (error.status == 404 && error.key == "USER_NOT_FOUND")) {
				error.dontShow =  true;
				this.logout();
			}
			this.emit("refresh-tokens-done", false);
			throw err;
		}

	};

	private onTokensRefreshed():Promise<boolean>{
		return new Promise((resolve:Function, reject:Function) => {
			this.once("refresh-tokens-done", (success:boolean) => {
				if (success) {
					resolve(success);
				}else{
					reject();
				}
			});
		});
	}

	async logout() {
		this.isLoggedIn = false;
		sessionStorage.clear();
		tokenStore.accessTokens = [];
		tokenStore.tokenExpires = null;
		tokenStore.refreshToken = null;
		tokenStore.refreshTokenExpires = null;
		AuthSocketService.broadcastToTabs("logout");
		SocketWorkerInterface.disconnectAll();
		await MainSocketService.disconnect();
		await AuthSocketService.disconnect();
		await router.push("/login");
	}

}

export const AuthService = new AuthServiceClass();